/**
 * CookieRegistry - Unified identity layer for live and replay modes.
 *
 * Maps callsigns to canonical cookies (`pilot_{callsign}`) so the same
 * pilot always gets the same marker regardless of whether data comes
 * from a live WebSocket (numeric cookie) or replay API (`replay_{id}`).
 *
 * Tracks bidirectional mappings:
 *   liveCookie  <-> callsign
 *   flightId    <-> callsign
 *   callsign    <-> canonical cookie
 */
export class CookieRegistry {
  constructor() {
    /** @type {Map<string, string>} callsign -> canonical cookie */
    this.callsignToCanonical = new Map();

    /** @type {Map<string, string>} liveCookie (numeric string) -> callsign */
    this.liveCookieToCallsign = new Map();

    /** @type {Map<string, string>} callsign -> liveCookie */
    this.callsignToLiveCookie = new Map();

    /** @type {Map<number, string>} flightId -> callsign */
    this.flightIdToCallsign = new Map();

    /** @type {Map<string, number>} callsign -> flightId */
    this.callsignToFlightId = new Map();
  }

  /**
   * Get (or create) the canonical cookie for a callsign.
   * Canonical format: `pilot_{callsign}`
   * @param {string} callsign
   * @returns {string} canonical cookie
   */
  getCanonical(callsign) {
    if (!callsign) return null;
    let canonical = this.callsignToCanonical.get(callsign);
    if (!canonical) {
      canonical = `pilot_${callsign}`;
      this.callsignToCanonical.set(callsign, canonical);
    }
    return canonical;
  }

  /**
   * Register a live WebSocket cookie -> callsign mapping.
   * Returns the canonical cookie that should replace the live cookie.
   * @param {string|number} liveCookie - server-assigned numeric cookie
   * @param {string} callsign
   * @returns {string} canonical cookie
   */
  registerLive(liveCookie, callsign) {
    if (!callsign || liveCookie == null) return null;
    const key = String(liveCookie);
    this.liveCookieToCallsign.set(key, callsign);
    this.callsignToLiveCookie.set(callsign, key);
    return this.getCanonical(callsign);
  }

  /**
   * Register a replay flight -> callsign mapping.
   * Returns the canonical cookie that should replace `replay_{id}`.
   * @param {number} flightId
   * @param {string} callsign
   * @returns {string} canonical cookie
   */
  registerFlight(flightId, callsign) {
    if (!callsign || flightId == null) return null;
    this.flightIdToCallsign.set(flightId, callsign);
    this.callsignToFlightId.set(callsign, flightId);
    return this.getCanonical(callsign);
  }

  /**
   * Resolve any cookie (live numeric, replay_*, or already canonical)
   * to its canonical form. Returns the original if no mapping exists.
   * @param {string|number} cookie
   * @returns {string}
   */
  resolve(cookie) {
    const key = String(cookie);

    // Already canonical
    if (key.startsWith('pilot_')) return key;

    // Live numeric cookie
    const callsignFromLive = this.liveCookieToCallsign.get(key);
    if (callsignFromLive) return this.getCanonical(callsignFromLive);

    // Replay cookie (replay_{flightId})
    if (key.startsWith('replay_')) {
      const flightId = parseInt(key.slice(7), 10);
      const callsignFromFlight = this.flightIdToCallsign.get(flightId);
      if (callsignFromFlight) return this.getCanonical(callsignFromFlight);
    }

    // Unknown — return as-is
    return key;
  }

  /**
   * Look up callsign from any cookie format.
   * @param {string|number} cookie
   * @returns {string|null}
   */
  getCallsign(cookie) {
    const key = String(cookie);

    if (key.startsWith('pilot_')) return key.slice(6);
    const fromLive = this.liveCookieToCallsign.get(key);
    if (fromLive) return fromLive;
    if (key.startsWith('replay_')) {
      const flightId = parseInt(key.slice(7), 10);
      return this.flightIdToCallsign.get(flightId) || null;
    }
    return null;
  }

  /**
   * Clear all mappings (e.g., on task deselection).
   */
  clear() {
    this.callsignToCanonical.clear();
    this.liveCookieToCallsign.clear();
    this.callsignToLiveCookie.clear();
    this.flightIdToCallsign.clear();
    this.callsignToFlightId.clear();
  }

  /**
   * Number of registered pilots.
   */
  get size() {
    return this.callsignToCanonical.size;
  }
}

// Singleton instance shared across the app
export const cookieRegistry = new CookieRegistry();
